import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Flex,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Stack,
  Alert,
  AlertIcon,
  SimpleGrid,
  useColorModeValue,
  Text,
  Select,
  HStack,
  Badge,
  Button,
  Spinner,
} from "@chakra-ui/react";

import NFT from "./components/NFT.js";
import imageNotFound from "assets/img/Image_not_available.png";

export default function Products() {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const userInfo = JSON.parse(localStorage.getItem("PAuser"));

  const [productList, setProductList] = useState([]);
  const [successAlert, setSuccesAlert] = useState("");
  const [failureAlert, setFailureAlert] = useState("");
  const [infringementFilter, setInfringementFilter] = useState("all"); // all | not_reported | reported | taken_down
  const [loading, setLoading] = useState(false);

  // pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;

  // bulk updates state
  const [pendingUpdates, setPendingUpdates] = useState([]);

  // cleanup alerts after timeout
  useEffect(() => {
    let timeout;
    if (successAlert || failureAlert) {
      timeout = setTimeout(() => {
        setSuccesAlert("");
        setFailureAlert("");
      }, 5000);
    }
    return () => clearTimeout(timeout);
  }, [successAlert, failureAlert]);

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchList = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/global-products/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid: userInfo?.message?.result.id }),
      });

      const json = await response.json();
      if (response.ok) {
        setProductList(Array.isArray(json) ? json : []);
      } else {
        setFailureAlert("Failed to load products");
      }
    } catch {
      setFailureAlert("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Generic updater
  const updateProduct = async ({ id, status, infringement_type }) => {
    try {
      const payload = { id };
      if (typeof status !== "undefined") payload.status = status;
      if (typeof infringement_type !== "undefined") payload.infringement_type = infringement_type;

      // instead of sending immediately, add to pendingUpdates
      setPendingUpdates((prev) => {
        const existing = prev.find((u) => u.id === id);
        if (existing) {
          return prev.map((u) => (u.id === id ? { ...u, ...payload } : u));
        } else {
          return [...prev, payload];
        }
      });

      // update UI optimistically
      setProductList((prev) =>
        prev.map((p) =>
          p.id === id
            ? {
              ...p,
              ...(typeof status !== "undefined" ? { status } : {}),
              ...(typeof infringement_type !== "undefined" ? { infringement_type } : {}),
            }
            : p
        )
      );
    } catch {
      setFailureAlert("An error occurred");
    }
  };

  // bulk update trigger
  const handleBulkSave = async () => {
    if (pendingUpdates.length === 0) return;
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/global-products/bulk-update-action`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates: pendingUpdates }),
      });

      if (response.ok) {
        setSuccesAlert("Bulk update successful");
        setPendingUpdates([]);
      } else {
        setFailureAlert("Bulk update failed");
      }
    } catch {
      setFailureAlert("An error occurred during bulk update");
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (id, status) => {
    await updateProduct({ id, status });
  };

  const handleSetInfringement = async (id, selectedTypesArray) => {
    const typesString = (selectedTypesArray && selectedTypesArray.length)
      ? selectedTypesArray.join(",")
      : "";
    await updateProduct({ id, status: 1, infringement_type: typesString });
  };

  const handleAddInfringeTypes = async (id, newTypesArray) => {
    if (!newTypesArray || newTypesArray.length === 0) return;
    const current = productList.find((p) => p.id === id);
    const currentTypes = (current?.infringement_type || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const set = new Set(currentTypes.map((t) => t.toLowerCase()));
    newTypesArray.forEach((t) => set.add(t.toLowerCase()));

    const merged = Array.from(set).map((t) =>
      t
        .split(" ")
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ")
    );

    await updateProduct({ id, infringement_type: merged.join(",") });
  };

  const handleMarkReported = async (id) => {
    await updateProduct({ id, status: 2 });
  };

  const handleMarkTakenDown = async (id) => {
    await updateProduct({ id, status: 3 });
  };

  // Categorize lists
  const identifiedProductList = useMemo(
    () => productList.filter((it) => it.status === null || typeof it.status === "undefined"),
    [productList]
  );
  const infringementAllList = useMemo(
    () => productList.filter((it) => [1, 2, 3].includes(it.status)),
    [productList]
  );
  const noInfringementProductList = useMemo(
    () => productList.filter((it) => it.status === 0),
    [productList]
  );
  const irrelevantProductList = useMemo(
    () => productList.filter((it) => it.status === -1),
    [productList]
  );

  const infringementProductList = useMemo(() => {
    switch (infringementFilter) {
      case "not_reported":
        return infringementAllList.filter((p) => p.status === 1);
      case "reported":
        return infringementAllList.filter((p) => p.status === 2);
      case "taken_down":
        return infringementAllList.filter((p) => p.status === 3);
      default:
        return infringementAllList;
    }
  }, [infringementAllList, infringementFilter]);

  // CSV download helpers
  const downloadCSV = (rows, filename) => {
    const blob = new Blob([rows], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportIdentifiedCSV = () => {
    const header = ["Sr No. ", "Keyword", "Title", "Platform", "Link", "Infringement Types"];
    const rows = identifiedProductList.map((p, index) => [
      (index + 1),
      p.keyword,
      p.title,
      p.platform,
      p.prod_link,
      p.infringement_type || "",
    ]);
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v || ""}"`).join(",")).join("\n");
    downloadCSV(csv, "identified_products.csv");
  };

  const exportInfringementCSV = () => {
    const header = ["Sr No.", "Keyword", "Title", "Platform", "Link", "Infringement Types", "Status"];
    const rows = infringementProductList.map((p, index) => {
      let statusText = "";
      if (p.status === 2) statusText = "Reported";
      if (p.status === 3) statusText = "Taken Down";
      return [
        (index + 1),
        p.keyword,
        p.title,
        p.platform,
        p.prod_link,
        p.infringement_type || "",
        statusText,
      ];
    });
    const csv = [header, ...rows].map((r) => r.map((v) => `"${v || ""}"`).join(",")).join("\n");
    downloadCSV(csv, "infringement_products.csv");
  };

  const paginatedItems = (items) => {
    const start = (currentPage - 1) * itemsPerPage;
    return items.slice(start, start + itemsPerPage);
  };

  const renderPaginationControls = (totalItems) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    if (totalPages <= 1) return null;
    return (
      <HStack justify="center" mt={4}>
        <Button size="sm" onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} disabled={currentPage === 1}>
          Prev
        </Button>
        <Text>
          Page {currentPage} of {totalPages}
        </Text>
        <Button
          size="sm"
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
        >
          Next
        </Button>
      </HStack>
    );
  };

  const renderGrid = (items, view) => (
    <>
      <SimpleGrid columns={{ base: 1, sm: 2, md: 3, xl: 4, "2xl": 5 }} spacing={5}>
        {paginatedItems(items).map((item, index) => (
          <NFT
            key={item.id ?? index}
            id={item.id}
            name={item.keyword}
            infringingName={item.title}
            image={item.image_link || imageNotFound}
            url={item.prod_link}
            platform={item.platform}
            clientStatus={item.status}
            infringementType={item.infringement_type}
            date={item.date}
            onInfringeWithTypes={handleSetInfringement}
            onAddMoreTypes={handleAddInfringeTypes}
            onNoInfringe={(id) => handleApproval(id, 0)}
            onIrrelevant={(id) => handleApproval(id, -1)}
            onMarkReported={handleMarkReported}
            onMarkTakenDown={handleMarkTakenDown}
            view={view}
          />
        ))}
      </SimpleGrid>
      {renderPaginationControls(items.length)}
    </>
  );

  return (
    <Tabs colorScheme="messenger" pt={{ base: "180px", md: "80px", xl: "80px" }}>
     
      <TabList>
        <Tab>
          Identified Products <Badge ml="2" colorScheme="gray">{identifiedProductList.length}</Badge>
        </Tab>
        <Tab>
          Infringements <Badge ml="2" colorScheme="red">{infringementAllList.length}</Badge>
        </Tab>
        <Tab>
          No Infringements <Badge ml="2" colorScheme="green">{noInfringementProductList.length}</Badge>
        </Tab>
        <Tab>
          Irrelevant Products <Badge ml="2" colorScheme="yellow">{irrelevantProductList.length}</Badge>
        </Tab>
      </TabList>
    


      <TabPanels>
        {/* Identified */}
        <TabPanel>
          <Flex justify="space-between" mb={4}>
            <Button size="sm" colorScheme="blue" onClick={exportIdentifiedCSV}>
              Get csv
            </Button>
            <Button size="sm" colorScheme="teal" onClick={handleBulkSave} disabled={pendingUpdates.length === 0}>
              Save Changes
            </Button>
          </Flex>
          {identifiedProductList.length === 0 && (
            <Box py={8} textAlign="center" color={textColor}>
              No Pending Products for Test Purchase approval
            </Box>
          )}
          {loading && (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          )}
          {renderGrid(identifiedProductList, "identified")}
        </TabPanel>

        {/* Infringements */}
        <TabPanel>
          <Flex justify="space-between" align="center" mb={4}>
            <HStack spacing={3}>
              <Text fontWeight="700" color={textColor}>Manage Infringements</Text>
              <Badge colorScheme="gray">{infringementProductList.length} items</Badge>
            </HStack>
            <HStack spacing={3}>
              <Select
                size="sm"
                value={infringementFilter}
                onChange={(e) => setInfringementFilter(e.target.value)}
                maxW="220px"
              >
                <option value="all">All</option>
                <option value="not_reported">Not Reported</option>
                <option value="reported">Reported</option>
                <option value="taken_down">Taken Down</option>
              </Select>
              <Button size="sm" colorScheme="blue" width="100%" onClick={exportInfringementCSV}>
                Get csv
              </Button>
              <Button size="sm" colorScheme="teal" width="100%" onClick={handleBulkSave} disabled={pendingUpdates.length === 0}>
                Save Changes
              </Button>
            </HStack>
          </Flex>

          {infringementProductList.length === 0 && (
            <Box py={8} textAlign="center" color={textColor}>
              No infringed products found for this filter...
            </Box>
          )}
          {loading && (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          )}
          {renderGrid(infringementProductList, "infringement")}
        </TabPanel>

        {/* No Infringements */}
        <TabPanel>
          <Flex justify="flex-end" mb={4}>
            <Button size="sm" colorScheme="teal" onClick={handleBulkSave} disabled={pendingUpdates.length === 0}>
              Save Changes
            </Button>
          </Flex>
          {noInfringementProductList.length === 0 && (
            <Box py={8} textAlign="center" color={textColor}>
              No products over here so far..
            </Box>
          )}
          {loading && (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          )}
          {renderGrid(noInfringementProductList, "noinfringe")}
        </TabPanel>

        {/* Irrelevant */}
        <TabPanel>
          <Flex justify="flex-end" mb={4}>
            <Button size="sm" colorScheme="teal" onClick={handleBulkSave} disabled={pendingUpdates.length === 0}>
              Save Changes
            </Button>
          </Flex>
          {irrelevantProductList.length === 0 && (
            <Box py={8} textAlign="center" color={textColor}>
              No irrelevant products so far...
            </Box>
          )}
          {loading && (
            <Flex justify="center" align="center" py={10}>
              <Spinner size="xl" />
            </Flex>
          )}
          {renderGrid(irrelevantProductList, "irrelevant")}
        </TabPanel>
      </TabPanels>
        <Stack spacing={3} position="fixed" right="10px" bottom="2vh" zIndex={1000}>
          {successAlert && (
            <Alert status="success" variant="left-accent" w={{ base: "90vw", md: "40vw" }}>
              <AlertIcon />
              {successAlert}
            </Alert>
          )}
          {failureAlert && (
            <Alert status="error" variant="left-accent" w={{ base: "90vw", md: "40vw" }}>
              <AlertIcon />
              {failureAlert}
            </Alert>
          )}
        </Stack>
      </Tabs>
    );
  }
